import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TelemetryLoggerService } from './telemetry-logger.service';

/**
 * GlobalErrorHandler
 *
 * A custom Angular ErrorHandler that captures all uncaught exceptions
 * and sends them to the telemetry backend.
 *
 * This captures:
 * - Uncaught exceptions in components
 * - Promise rejections that aren't caught
 * - Template errors
 * - Lifecycle hook errors
 *
 * Note: HTTP errors are handled separately by the HTTP interceptor.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  // Use Injector to avoid circular dependency issues
  private telemetryLogger: TelemetryLoggerService | null = null;

  constructor(
    private readonly injector: Injector,
    private readonly ngZone: NgZone
  ) {}

  /**
   * Get the telemetry logger lazily to avoid circular dependencies
   */
  private getLogger(): TelemetryLoggerService {
    return (this.telemetryLogger ??= this.injector.get(TelemetryLoggerService));
  }

  /**
   * Handle the error
   */
  handleError(error: unknown): void {
    // Run outside Angular zone to avoid triggering change detection
    this.ngZone.runOutsideAngular(() => {
      try {
        const logger = this.getLogger().forContext('GlobalErrorHandler');

        // Skip HTTP errors as they're handled by the interceptor
        if (error instanceof HttpErrorResponse) {
          // Still log to console for development
          console.error('[GlobalErrorHandler] HTTP Error (handled by interceptor):', error);
          return;
        }

        // Extract error details
        const errorDetails = this.extractErrorDetails(error);

        // Log the exception
        logger.trackException(error, {
          type: errorDetails.type,
          source: 'uncaught',
          componentStack: errorDetails.componentStack,
          ...errorDetails.additionalInfo,
        });

        // Also log to console in development
        console.error('[GlobalErrorHandler] Uncaught exception:', error);

      } catch (loggingError) {
        // If telemetry logging itself fails, at least log to console
        console.error('[GlobalErrorHandler] Failed to log error:', loggingError);
        console.error('[GlobalErrorHandler] Original error:', error);
      }
    });
  }

  /**
   * Extract detailed error information
   */
  private extractErrorDetails(error: unknown): {
    type: string;
    componentStack?: string;
    additionalInfo: Record<string, unknown>;
  } {
    const additionalInfo: Record<string, unknown> = {};

    // Handle different error types
    if (error instanceof Error) {
      // Check for Angular-specific error properties
      const angularError = error as Error & {
        ngOriginalError?: Error;
        ngDebugContext?: {
          component?: unknown;
          componentRenderElement?: Element;
        };
      };

      if (angularError.ngOriginalError) {
        additionalInfo['originalError'] = angularError.ngOriginalError.message;
      }

      const component = angularError.ngDebugContext?.component;
      if (component) {
        additionalInfo['component'] = this.coerceToString(component, 'UnknownComponent');
      }

      return {
        type: error.name || 'Error',
        componentStack: this.extractComponentStack(error),
        additionalInfo,
      };
    }

    // Handle promise rejections (often strings or plain objects)
    if (typeof error === 'string') {
      return {
        type: 'UnhandledRejection',
        additionalInfo: { message: error },
      };
    }

    if (error && typeof error === 'object') {
      const errorObj = error as Record<string, unknown>;
      return {
        type: this.coerceToString(errorObj['name'] ?? errorObj['type'], 'UnknownError'),
        additionalInfo: {
          message: errorObj['message'],
          ...errorObj,
        },
      };
    }

    return {
      type: 'UnknownError',
      additionalInfo: { rawError: this.coerceToString(error, 'UnknownError') },
    };
  }

  /**
   * Safely coerce an unknown value to a string, avoiding [object Object] output
   */
  private coerceToString(value: unknown, fallback: string): string {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return fallback;
  }

  /**
   * Extract component stack from error if available
   */
  private extractComponentStack(error: Error): string | undefined {
    // Angular sometimes includes component context in the stack
    if (error.stack) {
      // Look for Angular-specific patterns in the stack
      const angularStackMatch = error.stack.match(/at\s+(\w+Component)/g);
      if (angularStackMatch) {
        return angularStackMatch.join(' -> ');
      }
    }
    return undefined;
  }
}

/**
 * Setup global error listeners for the window
 * Call this function in your app initialization
 */
export function setupGlobalErrorListeners(telemetryLogger: TelemetryLoggerService): void {
  if (typeof globalThis === 'undefined' || typeof globalThis.addEventListener !== 'function') {
    return;
  }

  const logger = telemetryLogger.forContext('GlobalErrorListener');

  // Handle unhandled promise rejections
  globalThis.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    logger.trackException(event.reason, {
      type: 'UnhandledPromiseRejection',
      source: 'window',
    });
  });

  // Handle global errors (this is a fallback, most should be caught by Angular)
  globalThis.addEventListener('error', (event: ErrorEvent) => {
    // Avoid logging script errors from external sources
    if (event.filename && !event.filename.includes(globalThis.location.origin)) {
      logger.warn('External script error', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
      return;
    }

    logger.trackException(event.error || new Error(event.message), {
      type: 'GlobalError',
      source: 'window',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });
}
